import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import type { RecordsListFetchParams, StatusApiRow, StatusRow } from '@/types';
import { mapStatusRow } from '@/types';
import ApiRequest from '@/plugins/store/actions/api/ApiRequest';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
};

export const queryKeys = {
    getStatusRoot: ['status'],
    statusWithParams: (params: Ref<RecordsListFetchParams>) => [...queryKeys.getStatusRoot, params],
};

/**
 * Every status registration in the selected period and location scope, unpaged. The page derives its
 * header facet option sets and footer totals from this one array, so there is no server-side paging
 * or filtering contract to keep in step.
 *
 * Pig type is not sent: the caller narrows `locationIds` through
 * `getSelectedSiteIdsFilteredByPigType` before handing them over.
 *
 * The wire row is reshaped to `StatusRow` here rather than asserted directly, the same as the
 * Slaughters/Deaths hooks beside it - see `mapStatusRow` for why: keeping the reshape here isolates a
 * later wire field-name change to this call and to `mapStatusRow` itself.
 */
export function useStatusRecords(fetchParams: Ref<RecordsListFetchParams>, options: UseQueryOptions<StatusRow[]> = {}) {
    return useQuery<StatusRow[]>({
        queryKey: queryKeys.statusWithParams(fetchParams),
        // Request behaviour goes to the constructor; `send`'s third argument is the body.
        queryFn: () =>
            new ApiRequest({ skipLoadingIndicator: true })
                .send('get', `records/status?${fetchParams.value.period}&location_ids=${fetchParams.value.locationIds.join(',')}`)
                .then((rows: StatusApiRow[]) => rows.map(mapStatusRow)),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}
